import type {
  BucketRow,
  DeviceTraffic,
  PathTraffic,
  ReferrerTraffic,
  RegionTraffic,
  RetryBucket,
  LocalHourPoint,
  OriginCoverage,
  OriginInference,
  RestrictedAccessRow,
  RestrictedHit,
  LocaleSignalSummary,
  TrafficPoint,
  VpnNetwork,
  VpnRoute
} from '../types/traffic'

export interface TrafficSummary {
  totalRequests: number
  uniqueVisitors: number
  repeatRetryRate: number
  peakRps: number
  medianTimeOnPage: string
  supportEmailClicks: number
  /** Visitors seen in the last 5 minutes. */
  liveNow: number
  /** Mean dwell time in seconds across recorded events. */
  avgSecondsOnPage: number
  /** Average page loads per visitor — how often people come back to check. */
  viewsPerVisitor: number
  /** Share of visitors who clicked the support email link. */
  supportClickRate: number
  /** Share of visitors who returned more than once during the window. */
  returningShare: number
  /** Share of visitors who left within 10 seconds without retrying. */
  bounceRate: number
  /** Highest number of distinct visitors seen in a single 5-minute slice. */
  peakConcurrency: number
  /** Share of geo-resolved visitors likely on a VPN or proxy. Heuristic. */
  vpnSuspectedShare: number
  /** How many visitors that share is based on. */
  vpnResolvedVisitors: number
}

/** The JSON contract the Supabase aggregate function returns. */
export interface TrafficPayload {
  summary: TrafficSummary
  series: TrafficPoint[]
  /** Visitor time zones — a fallback proxy when edge geography is unavailable. */
  regions: RegionTraffic[]
  /** ISO country codes resolved at the edge. */
  countries: BucketRow[]
  /** Metro-level cities resolved at the edge. */
  cities: BucketRow[]
  paths: PathTraffic[]
  /** Requested URLs rolled up to their product area, e.g. '/dashboard'. */
  sections: BucketRow[]
  devices: DeviceTraffic[]
  referrers: ReferrerTraffic[]
  retryDistribution: RetryBucket[]
  browsers: BucketRow[]
  languages: BucketRow[]
  connections: BucketRow[]
  /** Physical medium — WiFi, Cellular, Ethernet. */
  mediums: BucketRow[]
  /** Networks that tunnelled traffic arrived on. */
  vpnNetworks: VpnNetwork[]
  /** Exit location vs the visitor's own browser time zone. */
  vpnRoutes: VpnRoute[]
  /** Requests by the visitor's own wall-clock hour. */
  localHours: LocalHourPoint[]
  /** Tunnelled visitors whose IP country disagrees with their inferred origin. */
  originInference: OriginInference[]
  /** Inferred origin across all visitors, tunnelled or not. */
  likelyOrigins: BucketRow[]
  /** Which inputs the origin comparison actually had available. */
  originCoverage: OriginCoverage
  /** How visitors are reaching the internet. */
  accessMethods: BucketRow[]
  /** Access method per inferred origin and exit country. */
  restrictedAccess: RestrictedAccessRow[]
  /** Screening results across the full visitor population. */
  restrictedHits: RestrictedHit[]
  /** How many jurisdictions the screen covers. */
  restrictedListSize: number
  /** Locale internals that survive time-zone spoofing. */
  localeSignals: LocaleSignalSummary
  /** Share of views that reported each attribute at all, 0–1. */
  coverage: {
    browser: number
    language: number
    connection: number
    medium: number
    geography: number
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

/** Numeric or explicitly absent — never NaN or undefined. */
function asNullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function normalizeHit(row: Record<string, unknown>): RestrictedHit {
  return {
    code: typeof row.code === 'string' ? row.code : '??',
    name: typeof row.name === 'string' ? row.name : 'Unknown',
    basis: typeof row.basis === 'string' ? row.basis : '',
    byIp: asNumber(row.byIp),
    byBrowserRegion: asNumber(row.byBrowserRegion),
    masked: asNumber(row.masked),
    maskedVia: Array.isArray(row.maskedVia)
      ? row.maskedVia.filter((via): via is string => typeof via === 'string')
      : [],
    browserZones: Array.isArray(row.browserZones)
      ? row.browserZones.filter((zone): zone is string => typeof zone === 'string')
      : []
  }
}

function normalizeLocaleSignals(value: unknown): LocaleSignalSummary {
  const raw = (value ?? {}) as Record<string, unknown>
  return {
    withSignals: asNumber(raw.withSignals),
    tension: asNumber(raw.tension),
    byOrigin: asArray<Record<string, unknown>>(raw.byOrigin).map(row => ({
      origin: typeof row.origin === 'string' ? row.origin : 'Unresolved',
      basis: typeof row.basis === 'string' ? row.basis : 'unknown',
      visitors: asNumber(row.visitors)
    })),
    byBasis: asArray<Record<string, unknown>>(raw.byBasis).map(row => ({
      basis: typeof row.basis === 'string' ? row.basis : 'unknown',
      visitors: asNumber(row.visitors)
    }))
  }
}

const CONFIDENCE = new Set(['high', 'medium', 'low'])

function normalizeOrigin(row: Record<string, unknown>): OriginInference {
  const confidence = typeof row.confidence === 'string' && CONFIDENCE.has(row.confidence)
    ? (row.confidence as OriginInference['confidence'])
    : 'low'

  return {
    exitCountry: typeof row.exitCountry === 'string' ? row.exitCountry : '??',
    exitCity: typeof row.exitCity === 'string' ? row.exitCity : 'Unknown city',
    likelyOrigin: typeof row.likelyOrigin === 'string' ? row.likelyOrigin : 'Unknown',
    confidence,
    basis: typeof row.basis === 'string' ? row.basis : 'time zone',
    browserTimeZone: typeof row.browserTimeZone === 'string' ? row.browserTimeZone : 'Unknown',
    networkBlocks: Array.isArray(row.networkBlocks)
      ? row.networkBlocks.filter((block): block is string => typeof block === 'string')
      : [],
    vpnFlagged: row.vpnFlagged === true,
    clockGapMinutes: asNullableNumber(row.clockGapMinutes),
    visitors: asNumber(row.visitors)
  }
}

/**
 * Rows written before the clock columns existed have no gap at all, so the value
 * is normalized to null here rather than leaking undefined into the table.
 */
function normalizeRoute(row: Record<string, unknown>): VpnRoute {
  return {
    exitCountry: typeof row.exitCountry === 'string' ? row.exitCountry : '??',
    exitCity: typeof row.exitCity === 'string' ? row.exitCity : 'Unknown city',
    browserTimeZone: typeof row.browserTimeZone === 'string' ? row.browserTimeZone : 'Unknown',
    clockGapMinutes: asNullableNumber(row.clockGapMinutes),
    visitors: asNumber(row.visitors),
    share: asNumber(row.share)
  }
}

/**
 * Validates and normalizes an aggregate response before it reaches the charts.
 * Missing optional sections become empty rather than crashing the dashboard, so
 * an older database function still renders what it does return.
 */
export function parseTrafficPayload(value: unknown): TrafficPayload {
  if (!isRecord(value)) throw new Error('Traffic response was not an object')

  const { summary } = value
  if (!isRecord(summary)) throw new Error('Traffic response is missing "summary"')
  if (!Array.isArray(value.series)) throw new Error('Traffic response is missing "series"')

  return {
    summary: {
      totalRequests: asNumber(summary.totalRequests),
      uniqueVisitors: asNumber(summary.uniqueVisitors),
      repeatRetryRate: asNumber(summary.repeatRetryRate),
      peakRps: asNumber(summary.peakRps),
      medianTimeOnPage:
        typeof summary.medianTimeOnPage === 'string' ? summary.medianTimeOnPage : '00:00',
      supportEmailClicks: asNumber(summary.supportEmailClicks),
      liveNow: asNumber(summary.liveNow),
      avgSecondsOnPage: asNumber(summary.avgSecondsOnPage),
      viewsPerVisitor: asNumber(summary.viewsPerVisitor),
      supportClickRate: asNumber(summary.supportClickRate),
      returningShare: asNumber(summary.returningShare),
      bounceRate: asNumber(summary.bounceRate),
      peakConcurrency: asNumber(summary.peakConcurrency),
      vpnSuspectedShare: asNumber(summary.vpnSuspectedShare),
      vpnResolvedVisitors: asNumber(summary.vpnResolvedVisitors)
    },
    series: asArray<TrafficPoint>(value.series),
    regions: asArray<RegionTraffic>(value.regions),
    countries: asArray<BucketRow>(value.countries),
    cities: asArray<BucketRow>(value.cities),
    paths: asArray<PathTraffic>(value.paths),
    sections: asArray<BucketRow>(value.sections),
    devices: asArray<DeviceTraffic>(value.devices),
    referrers: asArray<ReferrerTraffic>(value.referrers),
    retryDistribution: asArray<RetryBucket>(value.retryDistribution),
    browsers: asArray<BucketRow>(value.browsers),
    languages: asArray<BucketRow>(value.languages),
    connections: asArray<BucketRow>(value.connections),
    mediums: asArray<BucketRow>(value.mediums),
    vpnNetworks: asArray<VpnNetwork>(value.vpnNetworks),
    vpnRoutes: asArray<Record<string, unknown>>(value.vpnRoutes).map(normalizeRoute),
    localHours: asArray<LocalHourPoint>(value.localHours),
    originInference: asArray<Record<string, unknown>>(value.originInference).map(normalizeOrigin),
    likelyOrigins: asArray<BucketRow>(value.likelyOrigins),
    accessMethods: asArray<BucketRow>(value.accessMethods),
    restrictedAccess: asArray<RestrictedAccessRow>(value.restrictedAccess),
    restrictedHits: asArray<Record<string, unknown>>(value.restrictedHits).map(normalizeHit),
    restrictedListSize: asNumber(value.restrictedListSize),
    localeSignals: normalizeLocaleSignals(value.localeSignals),
    originCoverage: {
      withOrigin: isRecord(value.originCoverage) ? asNumber(value.originCoverage.withOrigin) : 0,
      withCountry: isRecord(value.originCoverage) ? asNumber(value.originCoverage.withCountry) : 0,
      comparable: isRecord(value.originCoverage) ? asNumber(value.originCoverage.comparable) : 0,
      matching: isRecord(value.originCoverage) ? asNumber(value.originCoverage.matching) : 0
    },
    coverage: {
      browser: isRecord(value.coverage) ? asNumber(value.coverage.browser) : 0,
      language: isRecord(value.coverage) ? asNumber(value.coverage.language) : 0,
      connection: isRecord(value.coverage) ? asNumber(value.coverage.connection) : 0,
      medium: isRecord(value.coverage) ? asNumber(value.coverage.medium) : 0,
      geography: isRecord(value.coverage) ? asNumber(value.coverage.geography) : 0
    }
  }
}
